import {
    collection,
    doc,
    setDoc,
    getDoc,
    getDocs,
    updateDoc,
    deleteDoc,
    query,
    where,
    orderBy,
    limit,
    onSnapshot,
    writeBatch
} from 'firebase/firestore';
import { db } from './config';
import { ChatSession, GlobalConfig, PromoCode, AdBanner, User, Notification, UserNotification } from '../../types';

export interface NotificationConfig {
    id: string;
    title: string;
    body: string;
    intervalMinutes: number;
    type: string;
    enabled: boolean;
    createdAt: number;
}

// ==================== GENERATED MATERIALS (QUIZZES & EXAMS) ====================

/**
 * Save a generated material (quiz or exam)
 */
export const saveGeneratedMaterial = async (material: any): Promise<void> => {
    try {
        const sanitizedMaterial: Record<string, any> = {
            id: material.id,
            userId: material.userId || null,
            type: material.type || 'unknown',
            title: material.title || 'Untitled',
            // Handle both data (legacy) and content (new) properties
            data: material.data || material.content || null,
            createdAt: material.createdAt || Date.now(),
            lastAccessed: Date.now(),
            topic: material.topic || null,
            difficulty: material.difficulty || null,
            itemCount: material.itemCount || null,
            sessionId: material.sessionId || null,
            tags: material.tags || []
        };

        // Strip any remaining undefined values — Firestore rejects them
        for (const key of Object.keys(sanitizedMaterial)) {
            if (sanitizedMaterial[key] === undefined) {
                sanitizedMaterial[key] = null;
            }
        }

        await setDoc(doc(db, 'generatedMaterials', material.id), sanitizedMaterial);
        console.log('Generated material saved:', material.id);
    } catch (error) {
        console.error('Error saving generated material:', error);
        throw error;
    }
};

/**
 * Get all generated materials for a user
 */
export const getUserGeneratedMaterials = async (userId: string): Promise<any[]> => {
    try {
        const materialsRef = collection(db, 'generatedMaterials');

        // Try with orderBy first (requires composite index)
        try {
            const q = query(materialsRef, where('userId', '==', userId), orderBy('createdAt', 'desc'));
            const snapshot = await getDocs(q);

            return snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
        } catch (indexError: any) {
            // If index doesn't exist, fall back to query without orderBy
            if (indexError.code === 'failed-precondition') {
                console.warn('Firebase index not created yet. Using simple query. Create index at:', indexError.message);
                const simpleQuery = query(materialsRef, where('userId', '==', userId));
                const snapshot = await getDocs(simpleQuery);

                // Sort in memory
                const materials = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));

                return materials.sort((a: any, b: any) => (b.createdAt || 0) - (a.createdAt || 0));
            }
            throw indexError;
        }
    } catch (error) {
        console.error('Error getting generated materials:', error);
        return [];
    }
};

/**
 * Delete a generated material
 */
export const deleteGeneratedMaterial = async (materialId: string): Promise<void> => {
    try {
        await deleteDoc(doc(db, 'generatedMaterials', materialId));
        console.log('Generated material deleted:', materialId);
    } catch (error) {
        console.error('Error deleting generated material:', error);
        throw error;
    }
};

/**
 * Listen to user's generated materials in real-time
 */
export const listenToUserMaterials = (userId: string, callback: (materials: any[]) => void) => {
    const materialsRef = collection(db, 'generatedMaterials');

    // Use simple query without orderBy to avoid index requirement
    // Sort will be done in memory
    const q = query(materialsRef, where('userId', '==', userId));

    return onSnapshot(q, (snapshot) => {
        const materials = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }));

        // Sort in memory by createdAt descending
        const sortedMaterials = materials.sort((a: any, b: any) => (b.createdAt || 0) - (a.createdAt || 0));
        callback(sortedMaterials);
    });
};

// ==================== CHAT SESSIONS ====================

/**
 * Deep sanitize an object to remove all undefined values
 * Firebase Firestore does NOT allow undefined values
 */
function deepSanitize(obj: any): any {
    if (obj === null || obj === undefined) {
        return null;
    }

    if (Array.isArray(obj)) {
        return obj.map(item => deepSanitize(item));
    }

    if (typeof obj === 'object' && !(obj instanceof Date)) {
        const sanitized: any = {};
        for (const key in obj) {
            if (obj.hasOwnProperty(key)) {
                const value = obj[key];
                // Only add if not undefined
                if (value !== undefined) {
                    sanitized[key] = deepSanitize(value);
                }
            }
        }
        return sanitized;
    }

    return obj;
}

// Track last log time for session saves to reduce console noise
const sessionSaveLogTimes = new Map<string, number>();

/**
 * Save a chat session
 */
export const saveChatSession = async (session: ChatSession): Promise<void> => {
    try {
        // Sanitize session data before saving - remove ALL undefined values
        const sanitizedSession: any = {
            id: session.id,
            title: session.title || 'Untitled Session',
            userId: session.userId !== undefined ? session.userId : null,
            createdAt: Number.isFinite(session.createdAt) ? session.createdAt : Date.now(),
            lastModified: Date.now(),
            pinned: session.pinned === true, // Convert to boolean, default false
            // IMPORTANT: Include pinned messages and generated materials
            pinnedMessages: Array.isArray(session.pinnedMessages) ? session.pinnedMessages : [],
            generatedMaterials: Array.isArray(session.generatedMaterials) ? session.generatedMaterials : [],
            tags: Array.isArray(session.tags) ? session.tags : [],
            messages: (session.messages || []).map(msg => {
                const sanitizedMsg: any = {
                    id: msg.id,
                    role: msg.role,
                    content: msg.content || '',
                    timestamp: ((msg.timestamp as any) instanceof Date && !isNaN((msg.timestamp as any).getTime()))
                        ? (msg.timestamp as any).toISOString()
                        : (typeof msg.timestamp === 'string' ? msg.timestamp : new Date().toISOString())
                };

                // Only add optional fields if they exist and are NOT undefined
                if (msg.thought !== undefined && msg.thought !== null) sanitizedMsg.thought = msg.thought;
                if (msg.thoughtProcess !== undefined && msg.thoughtProcess !== null) sanitizedMsg.thoughtProcess = msg.thoughtProcess;
                if (msg.isStreaming === true || msg.isStreaming === false) sanitizedMsg.isStreaming = msg.isStreaming;
                if (msg.isError === true || msg.isError === false) sanitizedMsg.isError = msg.isError;
                if (msg.feedback && (msg.feedback === 'up' || msg.feedback === 'down')) sanitizedMsg.feedback = msg.feedback;
                if (msg.attachments && Array.isArray(msg.attachments) && msg.attachments.length > 0) {
                    // Sanitize attachments to remove undefined values
                    sanitizedMsg.attachments = msg.attachments.map(att => {
                        const sanitizedAtt: any = {
                            id: att.id,
                            name: att.name || 'unnamed',
                            mimeType: att.mimeType || 'application/octet-stream',
                            data: att.data
                        };

                        // Only add optional fields if they are NOT undefined
                        if (att.preview !== undefined && att.preview !== null) sanitizedAtt.preview = att.preview;
                        if (att.annotations !== undefined && att.annotations !== null) sanitizedAtt.annotations = att.annotations;
                        if (att.extractedText !== undefined && att.extractedText !== null) sanitizedAtt.extractedText = att.extractedText;
                        if (att.size !== undefined && typeof att.size === 'number') sanitizedAtt.size = att.size;

                        return sanitizedAtt;
                    });
                }
                if (typeof msg.tokenCost === 'number' && !isNaN(msg.tokenCost)) sanitizedMsg.tokenCost = msg.tokenCost;
                if (typeof msg.tokensCharged === 'number' && !isNaN(msg.tokensCharged)) sanitizedMsg.tokensCharged = msg.tokensCharged;
                if (msg.hasWhiteboardContent === true || msg.hasWhiteboardContent === false) sanitizedMsg.hasWhiteboardContent = msg.hasWhiteboardContent;
                if (msg.whiteboardContent !== undefined && msg.whiteboardContent !== null) sanitizedMsg.whiteboardContent = msg.whiteboardContent;
                if (msg.referencedAttachments && Array.isArray(msg.referencedAttachments) && msg.referencedAttachments.length > 0) {
                    sanitizedMsg.referencedAttachments = msg.referencedAttachments;
                }
                if (typeof msg.budgetAdjusted === 'number' && !isNaN(msg.budgetAdjusted)) sanitizedMsg.budgetAdjusted = msg.budgetAdjusted;
                if (msg.modelUsed !== undefined && msg.modelUsed !== null) sanitizedMsg.modelUsed = msg.modelUsed;
                if (msg.persona !== undefined && msg.persona !== null) sanitizedMsg.persona = msg.persona;
                if (msg.depth !== undefined && msg.depth !== null) sanitizedMsg.depth = msg.depth;

                return sanitizedMsg;
            })
        };

        // Add optional session-level fields only if they're not undefined
        if (session.lastSyncedAt !== undefined && typeof session.lastSyncedAt === 'number') {
            sanitizedSession.lastSyncedAt = session.lastSyncedAt;
        }
        if (session.syncStatus !== undefined && session.syncStatus !== null) {
            sanitizedSession.syncStatus = session.syncStatus;
        }
        if (session.version !== undefined && typeof session.version === 'number') {
            sanitizedSession.version = session.version;
        }

        // FINAL DEEP SANITIZATION: Remove any remaining undefined values recursively
        const fullySanitized = deepSanitize(sanitizedSession);

        // Check document size (Firebase limit is 1MB = 1,048,576 bytes)
        const estimatedSize = new Blob([JSON.stringify(fullySanitized)]).size;
        const MAX_SIZE = 1048576; // 1MB in bytes
        const SAFE_SIZE = 900000; // 900KB to leave some buffer

        if (estimatedSize > SAFE_SIZE) {
            console.warn(`Session ${session.id} size (${estimatedSize} bytes) exceeds safe limit. Trimming aggressively...`);

            let trimmedMessages = [...fullySanitized.messages];
            let currentSize = estimatedSize;

            // AGGRESSIVE TRIMMING: Remove attachments from VERY old messages (keep last 15 with attachments)
            console.log(`Session ${session.id} too large. Removing old attachments...`);
            trimmedMessages = trimmedMessages.map((msg, idx) => {
                if (idx < trimmedMessages.length - 15 && msg.attachments) {
                    // Remove attachments from older messages (keep last 15)
                    const { attachments, ...msgWithoutAttachments } = msg;
                    return msgWithoutAttachments;
                }
                return msg;
            });

            currentSize = new Blob([JSON.stringify({ ...fullySanitized, messages: trimmedMessages })]).size;
            console.log(`After removing old attachments: ${currentSize} bytes with ${trimmedMessages.length} messages`);

            // If STILL too large, remove oldest messages
            while (currentSize > SAFE_SIZE && trimmedMessages.length > 5) {
                trimmedMessages.shift(); // Remove oldest message
                const testSession = { ...fullySanitized, messages: trimmedMessages };
                currentSize = new Blob([JSON.stringify(testSession)]).size;
            }

            // If STILL too large after everything, remove attachments from ALL messages
            if (currentSize > SAFE_SIZE) {
                console.warn(`Session ${session.id} STILL too large. Removing ALL attachments.`);
                trimmedMessages = trimmedMessages.map(msg => {
                    const { attachments, ...msgWithoutAttachments } = msg;
                    return msgWithoutAttachments;
                });
                currentSize = new Blob([JSON.stringify({ ...fullySanitized, messages: trimmedMessages })]).size;
            }

            // Final safety: If STILL too large, keep only last 3 messages
            if (currentSize > SAFE_SIZE) {
                console.error(`Session ${session.id} critically large. Keeping only last 3 messages.`);
                trimmedMessages = trimmedMessages.slice(-3);
                currentSize = new Blob([JSON.stringify({ ...fullySanitized, messages: trimmedMessages })]).size;
            }

            const finalSession = deepSanitize({ ...fullySanitized, messages: trimmedMessages });
            console.log(`✅ Trimmed session to ${trimmedMessages.length} messages (${currentSize} bytes)`);

            await setDoc(doc(db, 'chatSessions', session.id), finalSession);
        } else {
            // Save the full sanitized session
            await setDoc(doc(db, 'chatSessions', session.id), fullySanitized);
        }
        // Debounced logging - only log once per session per 10 seconds
        const now = Date.now();
        const lastLog = sessionSaveLogTimes.get(session.id) || 0;
        if (now - lastLog > 10000) {
            console.log('Chat session saved successfully:', session.id);
            sessionSaveLogTimes.set(session.id, now);
        }
    } catch (error) {
        console.error('Error saving chat session:', error);
        throw error;
    }
};

/**
 * Get a chat session by ID
 */
export const getChatSession = async (sessionId: string): Promise<ChatSession | null> => {
    try {
        const sessionDoc = await getDoc(doc(db, 'chatSessions', sessionId));
        if (sessionDoc.exists()) {
            const data = sessionDoc.data() as any;
            // Hydrate timestamp objects from numbers
            return {
                ...data,
                // Ensure arrays are initialized
                pinnedMessages: Array.isArray(data.pinnedMessages) ? data.pinnedMessages : [],
                generatedMaterials: Array.isArray(data.generatedMaterials) ? data.generatedMaterials : [],
                tags: Array.isArray(data.tags) ? data.tags : [],
                messages: (data.messages || []).map((m: any) => ({
                    ...m,
                    timestamp: typeof m.timestamp === 'number' ? new Date(m.timestamp) : new Date(m.timestamp)
                }))
            };
        }
        return null;
    } catch (error) {
        console.error('Error getting chat session:', error);
        throw error;
    }
};

/**
 * Get all chat sessions for a user
 */
export const getUserChatSessions = async (userId?: string): Promise<ChatSession[]> => {
    try {
        let q;
        if (userId) {
            q = query(
                collection(db, 'chatSessions'),
                where('userId', '==', userId),
                orderBy('lastModified', 'desc')
            );
        } else {
            // For guest users (no userId)
            q = query(
                collection(db, 'chatSessions'),
                where('userId', '==', null),
                orderBy('lastModified', 'desc')
            );
        }

        const querySnapshot = await getDocs(q);
        return querySnapshot.docs.map(doc => {
            const data = doc.data() as any;
            return {
                ...data,
                // Ensure arrays are initialized
                pinnedMessages: Array.isArray(data.pinnedMessages) ? data.pinnedMessages : [],
                generatedMaterials: Array.isArray(data.generatedMaterials) ? data.generatedMaterials : [],
                tags: Array.isArray(data.tags) ? data.tags : [],
                messages: (data.messages || []).map((m: any) => ({
                    ...m,
                    timestamp: typeof m.timestamp === 'number' ? new Date(m.timestamp) : new Date(m.timestamp)
                }))
            };
        });
    } catch (error) {
        console.error('Error getting user chat sessions:', error);
        throw error;
    }
};

/**
 * Get all chat sessions (admin only)
 */
export const getAllChatSessions = async (): Promise<ChatSession[]> => {
    try {
        const q = query(collection(db, 'chatSessions'), orderBy('lastModified', 'desc'));
        const querySnapshot = await getDocs(q);
        return querySnapshot.docs.map(doc => {
            const data = doc.data() as any;
            return {
                ...data,
                messages: data.messages.map((m: any) => ({
                    ...m,
                    timestamp: typeof m.timestamp === 'number' ? new Date(m.timestamp) : new Date(m.timestamp)
                }))
            };
        });
    } catch (error) {
        console.error('Error getting all chat sessions:', error);
        throw error;
    }
};

/**
 * Update a chat session
 */
export const updateChatSession = async (sessionId: string, updates: Partial<ChatSession>): Promise<void> => {
    try {
        await updateDoc(doc(db, 'chatSessions', sessionId), updates as any);
        console.log('Chat session updated successfully:', sessionId);
    } catch (error) {
        console.error('Error updating chat session:', error);
        throw error;
    }
};

/**
 * Delete a chat session
 */
export const deleteChatSession = async (sessionId: string): Promise<void> => {
    try {
        await deleteDoc(doc(db, 'chatSessions', sessionId));
        console.log('Chat session deleted successfully:', sessionId);
    } catch (error) {
        console.error('Error deleting chat session:', error);
        throw error;
    }
};

// ==================== GLOBAL CONFIG ====================

/**
 * Save global configuration
 */
export const saveGlobalConfig = async (config: GlobalConfig): Promise<void> => {
    try {
        await setDoc(doc(db, 'config', 'global'), config);
        console.log('Global config saved successfully');
    } catch (error) {
        console.error('Error saving global config:', error);
        throw error;
    }
};

/**
 * Get global configuration
 */
export const getGlobalConfig = async (): Promise<GlobalConfig | null> => {
    try {
        const configDoc = await getDoc(doc(db, 'config', 'global'));
        if (configDoc.exists()) {
            return configDoc.data() as GlobalConfig;
        }
        return null;
    } catch (error) {
        console.error('Error getting global config:', error);
        throw error;
    }
};

// ==================== PROMO CODES ====================

/**
 * Save promo codes
 */
export const savePromoCodes = async (promoCodes: PromoCode[]): Promise<void> => {
    try {
        await setDoc(doc(db, 'config', 'promoCodes'), { codes: promoCodes });
        console.log('Promo codes saved successfully');
    } catch (error) {
        console.error('Error saving promo codes:', error);
        throw error;
    }
};

/**
 * Get promo codes
 */
export const getPromoCodes = async (): Promise<PromoCode[]> => {
    try {
        const promoDoc = await getDoc(doc(db, 'config', 'promoCodes'));
        if (promoDoc.exists()) {
            return (promoDoc.data() as any).codes || [];
        }
        return [];
    } catch (error) {
        console.error('Error getting promo codes:', error);
        throw error;
    }
};

/**
 * Save a single promo code (adds to existing codes)
 */
export const savePromoCode = async (promoCode: PromoCode): Promise<void> => {
    try {
        const existingCodes = await getPromoCodes();
        const updatedCodes = [...existingCodes, promoCode];
        await setDoc(doc(db, 'config', 'promoCodes'), { codes: updatedCodes });
        console.log('Promo code saved successfully:', promoCode.code);
    } catch (error) {
        console.error('Error saving promo code:', error);
        throw error;
    }
};

// ==================== PAYMONGO CONFIG ====================

/**
 * Save PayMongo configuration
 */
export const savePayMongoConfig = async (config: any): Promise<void> => {
    try {
        await setDoc(doc(db, 'config', 'paymongo'), config);
        console.log('PayMongo config saved successfully');
    } catch (error) {
        console.error('Error saving PayMongo config:', error);
        throw error;
    }
};

/**
 * Get PayMongo configuration
 */
export const getPayMongoConfig = async (): Promise<any | null> => {
    try {
        const configDoc = await getDoc(doc(db, 'config', 'paymongo'));
        if (configDoc.exists()) {
            return configDoc.data();
        }
        return null;
    } catch (error) {
        console.error('Error getting PayMongo config:', error);
        return null;
    }
};

// ==================== READABLES TOKEN CONFIG ====================

/**
 * Save Readables token configuration to Firestore
 */
export const saveReadablesTokenConfigToFirebase = async (config: any): Promise<void> => {
    try {
        await setDoc(doc(db, 'config', 'readablesTokens'), { ...config, updatedAt: Date.now() });
        console.log('Readables token config saved successfully');
    } catch (error) {
        console.error('Error saving readables token config:', error);
        throw error;
    }
};

/**
 * Get Readables token configuration from Firestore
 */
export const getReadablesTokenConfig = async (): Promise<any | null> => {
    try {
        const configDoc = await getDoc(doc(db, 'config', 'readablesTokens'));
        if (configDoc.exists()) {
            return configDoc.data();
        }
        return null;
    } catch (error) {
        console.error('Error getting readables token config:', error);
        return null;
    }
};

// ==================== QUICKBITES TOKEN CONFIG ====================

/**
 * Save QuickBites token configuration to Firestore
 */
export const saveQuickBitesTokenConfigToFirebase = async (config: any): Promise<void> => {
    try {
        await setDoc(doc(db, 'config', 'quickBitesTokens'), { ...config, updatedAt: Date.now() });
        console.log('QuickBites token config saved successfully');
    } catch (error) {
        console.error('Error saving quickBites token config:', error);
        throw error;
    }
};

/**
 * Get QuickBites token configuration from Firestore
 */
export const getQuickBitesTokenConfig = async (): Promise<any | null> => {
    try {
        const configDoc = await getDoc(doc(db, 'config', 'quickBitesTokens'));
        if (configDoc.exists()) {
            return configDoc.data();
        }
        return null;
    } catch (error) {
        console.error('Error getting quickBites token config:', error);
        return null;
    }
};

// ==================== ADS ====================

/**
 * Save ads
 */
export const saveAds = async (ads: AdBanner[]): Promise<void> => {
    try {
        // Sanitize ads to remove undefined values
        const sanitizedAds = ads.map(ad => {
            const cleanAd: any = {};
            for (const [key, value] of Object.entries(ad)) {
                if (value !== undefined) {
                    cleanAd[key] = value;
                }
            }
            return cleanAd;
        });
        await setDoc(doc(db, 'config', 'ads'), { banners: sanitizedAds });
        console.log('Ads saved successfully');
    } catch (error) {
        console.error('Error saving ads:', error);
        throw error;
    }
};

/**
 * Get ads
 */
export const getAds = async (): Promise<AdBanner[]> => {
    try {
        const adsDoc = await getDoc(doc(db, 'config', 'ads'));
        if (adsDoc.exists()) {
            return (adsDoc.data() as any).banners || [];
        }
        return [];
    } catch (error) {
        console.error('Error getting ads:', error);
        throw error;
    }
};

// ==================== USER PREFERENCES ====================

/**
 * Save user preferences (density, dark mode, etc.)
 */
export const saveUserPreferences = async (userId: string, preferences: {
    messageDensity?: string;
    isDarkMode?: boolean;
}): Promise<void> => {
    try {
        await setDoc(doc(db, 'userPreferences', userId), preferences, { merge: true });
        console.log('User preferences saved successfully:', userId);
    } catch (error) {
        console.error('Error saving user preferences:', error);
        throw error;
    }
};

/**
 * Get user preferences
 */
export const getUserPreferences = async (userId: string): Promise<any> => {
    try {
        const prefDoc = await getDoc(doc(db, 'userPreferences', userId));
        if (prefDoc.exists()) {
            return prefDoc.data();
        }
        return null;
    } catch (error) {
        console.error('Error getting user preferences:', error);
        throw error;
    }
};

// ==================== SUPPORT TICKETS & PURCHASES ====================

/**
 * Save a support ticket to Firestore
 */
export const saveSupportTicket = async (ticket: any): Promise<void> => {
    try {
        // Remove undefined fields to prevent Firebase errors
        const cleanTicket = Object.fromEntries(
            Object.entries({
                ...ticket,
                createdAt: ticket.createdAt || new Date().toISOString(),
                updatedAt: ticket.updatedAt || new Date().toISOString()
            }).filter(([_, value]) => value !== undefined)
        );

        await setDoc(doc(db, 'supportTickets', ticket.id), cleanTicket);
        console.log('Support ticket saved to Firestore:', ticket.id);
    } catch (error) {
        console.error('Error saving support ticket:', error);
        throw error;
    }
};

/**
 * Get all support tickets (admin)
 */
export const getAllSupportTickets = async (): Promise<any[]> => {
    try {
        const q = query(collection(db, 'supportTickets'), orderBy('createdAt', 'desc'));
        const querySnapshot = await getDocs(q);
        return querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }));
    } catch (error) {
        console.error('Error getting support tickets:', error);
        return [];
    }
};

/**
 * Get support tickets by user
 */
export const getUserSupportTickets = async (userId: string): Promise<any[]> => {
    try {
        const q = query(
            collection(db, 'supportTickets'),
            where('userId', '==', userId),
            orderBy('createdAt', 'desc')
        );
        const querySnapshot = await getDocs(q);
        return querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }));
    } catch (error) {
        console.error('Error getting user support tickets:', error);
        return [];
    }
};

/**
 * Deep clean object to remove all undefined and null values
 */
const deepCleanObject = (obj: any): any => {
    if (obj === null || obj === undefined) return null;

    if (Array.isArray(obj)) {
        return obj.map(item => deepCleanObject(item)).filter(item => item !== null && item !== undefined);
    }

    if (typeof obj === 'object') {
        const cleaned: any = {};
        for (const [key, value] of Object.entries(obj)) {
            if (value === undefined || value === null) continue;

            if (typeof value === 'object' && !Array.isArray(value)) {
                const cleanedValue = deepCleanObject(value);
                if (cleanedValue && Object.keys(cleanedValue).length > 0) {
                    cleaned[key] = cleanedValue;
                }
            } else if (Array.isArray(value)) {
                const cleanedArray = deepCleanObject(value);
                if (cleanedArray && cleanedArray.length > 0) {
                    cleaned[key] = cleanedArray;
                }
            } else {
                cleaned[key] = value;
            }
        }
        return cleaned;
    }

    return obj;
};

/**
 * Update a support ticket
 */
export const updateSupportTicket = async (ticketId: string, updates: any): Promise<void> => {
    try {
        // Deep clean all undefined/null values recursively
        const cleanedUpdates = deepCleanObject({
            ...updates,
            updatedAt: new Date().toISOString()
        });

        if (!cleanedUpdates || Object.keys(cleanedUpdates).length === 0) {
            console.warn(`[Firebase] No valid updates for ticket ${ticketId}`);
            return;
        }

        // Check if document exists first
        const docRef = doc(db, 'supportTickets', ticketId);
        const docSnap = await getDoc(docRef);

        if (!docSnap.exists()) {
            console.warn(`[Firebase] Document ${ticketId} doesn't exist, skipping update`);
            return;
        }

        await updateDoc(docRef, cleanedUpdates);
        console.log('Support ticket updated in Firestore:', ticketId);
    } catch (error) {
        console.error('Error updating support ticket:', error);
        throw error;
    }
};

/**
 * Save a purchase record to Firestore
 */
export const savePurchaseRecord = async (purchase: any): Promise<void> => {
    try {
        await setDoc(doc(db, 'purchaseRecords', purchase.id), {
            ...purchase,
            timestamp: purchase.timestamp || new Date().toISOString()
        });
        console.log('Purchase record saved to Firestore:', purchase.id, 'with support code:', purchase.supportCode);
    } catch (error) {
        console.error('Error saving purchase record:', error);
        throw error;
    }
};

/**
 * Save support action log for admin visibility
 */
export const saveSupportLog = async (log: any): Promise<void> => {
    try {
        const logId = `${log.ticketId}-${Date.now()}`;
        // Sanitize log to remove undefined values
        const sanitizedLog = deepCleanObject({
            ...log,
            timestamp: log.timestamp || new Date().toISOString()
        });

        await setDoc(doc(db, 'supportLogs', logId), sanitizedLog);
        console.log('Support log saved to Firestore:', logId);
    } catch (error) {
        console.error('Error saving support log:', error);
        // Don't throw - logging should not break main flow
    }
};

/**
 * Get support logs for a ticket
 */
export const getSupportLogs = async (ticketId: string): Promise<any[]> => {
    try {
        const q = query(
            collection(db, 'supportLogs'),
            where('ticketId', '==', ticketId),
            orderBy('timestamp', 'desc')
        );
        const querySnapshot = await getDocs(q);
        return querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }));
    } catch (error) {
        console.error('Error getting support logs:', error);
        return [];
    }
};

/**
 * Listen to support logs in real-time
 */
export const listenToSupportLogs = (callback: (logs: any[]) => void): (() => void) => {
    try {
        const q = query(
            collection(db, 'supportLogs'),
            orderBy('timestamp', 'desc'),
            limit(100)
        );

        return onSnapshot(q, (snapshot) => {
            const logs = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            callback(logs);
        }, (error) => {
            console.error('Error listening to support logs:', error);
            callback([]);
        });
    } catch (error) {
        console.error('Error setting up support logs listener:', error);
        return () => { };
    }
};

/**
 * Get all purchase records (admin)
 */
export const getAllPurchaseRecords = async (): Promise<any[]> => {
    try {
        const q = query(collection(db, 'purchaseRecords'), orderBy('timestamp', 'desc'));
        const querySnapshot = await getDocs(q);
        return querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }));
    } catch (error) {
        console.error('Error getting purchase records:', error);
        return [];
    }
};

/**
 * Get purchase records by user
 */
export const getUserPurchaseRecords = async (userId: string): Promise<any[]> => {
    try {
        const q = query(
            collection(db, 'purchaseRecords'),
            where('userId', '==', userId),
            orderBy('timestamp', 'desc')
        );
        const querySnapshot = await getDocs(q);
        return querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }));
    } catch (error) {
        console.error('Error getting user purchase records:', error);
        return [];
    }
};

/**
 * Get purchase by support code
 */
export const getPurchaseBySupportCode = async (supportCode: string): Promise<any | null> => {
    try {
        const q = query(
            collection(db, 'purchaseRecords'),
            where('supportCode', '==', supportCode)
        );
        const querySnapshot = await getDocs(q);
        if (!querySnapshot.empty) {
            const doc = querySnapshot.docs[0];
            return {
                id: doc.id,
                ...doc.data()
            };
        }
        return null;
    } catch (error) {
        console.error('Error getting purchase by support code:', error);
        return null;
    }
};

/**
 * Listen to support tickets in real-time (admin)
 */
export const listenToSupportTickets = (
    callback: (tickets: any[]) => void
): (() => void) => {
    try {
        const q = query(collection(db, 'supportTickets'), orderBy('createdAt', 'desc'));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const tickets = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            callback(tickets);
        }, (error) => {
            console.error('Error listening to support tickets:', error);
            callback([]);
        });

        return unsubscribe;
    } catch (error) {
        console.error('Error setting up support tickets listener:', error);
        return () => { };
    }
};

/**
 * Listen to purchase records in real-time (admin)
 */
export const listenToPurchaseRecords = (
    callback: (purchases: any[]) => void
): (() => void) => {
    try {
        const q = query(collection(db, 'purchaseRecords'), orderBy('timestamp', 'desc'));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const purchases = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            callback(purchases);
        }, (error) => {
            console.error('Error listening to purchase records:', error);
            callback([]);
        });

        return unsubscribe;
    } catch (error) {
        console.error('Error setting up purchase records listener:', error);
        return () => { };
    }
};

// ==================== REAL-TIME LISTENERS ====================

/**
 * Listen to chat sessions changes
 */
export const listenToUserChatSessions = (userId: string | undefined, callback: (sessions: ChatSession[]) => void) => {
    let q;
    if (userId) {
        q = query(
            collection(db, 'chatSessions'),
            where('userId', '==', userId),
            orderBy('lastModified', 'desc')
        );
    } else {
        q = query(
            collection(db, 'chatSessions'),
            where('userId', '==', null),
            orderBy('lastModified', 'desc')
        );
    }

    return onSnapshot(q, (snapshot) => {
        const sessions = snapshot.docs.map(doc => {
            const data = doc.data() as any;
            return {
                ...data,
                // Ensure arrays are initialized even if missing from Firestore
                pinnedMessages: Array.isArray(data.pinnedMessages) ? data.pinnedMessages : [],
                generatedMaterials: Array.isArray(data.generatedMaterials) ? data.generatedMaterials : [],
                tags: Array.isArray(data.tags) ? data.tags : [],
                messages: (data.messages || []).map((m: any) => ({
                    ...m,
                    timestamp: typeof m.timestamp === 'number' ? new Date(m.timestamp) : new Date(m.timestamp)
                }))
            };
        });
        callback(sessions);
    });
};

/**
 * Listen to all chat sessions (Admin)
 */
export const listenToAllChatSessions = (callback: (sessions: ChatSession[]) => void) => {
    const q = query(collection(db, 'chatSessions'), orderBy('lastModified', 'desc'));
    return onSnapshot(q, (snapshot) => {
        const sessions = snapshot.docs.map(doc => {
            const data = doc.data() as any;
            return {
                ...data,
                // Ensure arrays are initialized even if missing from Firestore
                pinnedMessages: Array.isArray(data.pinnedMessages) ? data.pinnedMessages : [],
                generatedMaterials: Array.isArray(data.generatedMaterials) ? data.generatedMaterials : [],
                tags: Array.isArray(data.tags) ? data.tags : [],
                messages: (data.messages || []).map((m: any) => ({
                    ...m,
                    timestamp: typeof m.timestamp === 'number' ? new Date(m.timestamp) : new Date(m.timestamp)
                }))
            };
        });
        callback(sessions);
    });
};

/**
 * Listen to all transactions (Admin)
 */
export const listenToAllTransactions = (callback: (transactions: any[]) => void) => {
    const q = query(collection(db, 'transactions'), orderBy('createdAt', 'desc'));
    return onSnapshot(q, (snapshot) => {
        const transactions = snapshot.docs.map(doc => doc.data());
        callback(transactions);
    });
};

/**
 * Listen to global config changes
 */
export const listenToGlobalConfig = (callback: (config: GlobalConfig | null) => void) => {
    return onSnapshot(doc(db, 'config', 'global'), (doc) => {
        if (doc.exists()) {
            const data = doc.data();
            // Ensure all required fields are present or have defaults
            const config: GlobalConfig = {
                freeDailyLimit: data.freeDailyLimit ?? 10,
                freeTokenLimit: data.freeTokenLimit ?? 50000,
                freeAttachmentLimit: data.freeAttachmentLimit ?? 5,
                freeModel: data.freeModel ?? 'gemini-pro',
                premiumModel: data.premiumModel ?? 'gemini-pro',
                proPrice: data.proPrice ?? 9.99,
                isSaleActive: data.isSaleActive ?? false,
                saleDiscountPercent: data.saleDiscountPercent ?? 0,
                ...data
            } as GlobalConfig;
            callback(config);
        } else {
            callback(null);
        }
    });
};

/**
 * Listen to promo codes
 */
export const listenToPromoCodes = (callback: (promos: PromoCode[]) => void) => {
    return onSnapshot(doc(db, 'config', 'promoCodes'), (doc) => {
        if (doc.exists()) {
            callback((doc.data() as any).codes || []);
        } else {
            callback([]);
        }
    });
};

/**
 * Listen to ads
 */
export const listenToAds = (callback: (ads: AdBanner[]) => void) => {
    return onSnapshot(doc(db, 'config', 'ads'), (doc) => {
        if (doc.exists()) {
            callback((doc.data() as any).banners || []);
        } else {
            callback([]);
        }
    });
};

// ==================== MIGRATION HELPER ====================

/**
 * Migrate data from localStorage to Firebase
 * This should be called once to move existing data
 */
export const migrateFromLocalStorage = async (): Promise<void> => {
    try {
        console.log('Starting migration from localStorage to Firebase...');

        // Migrate users
        const usersData = localStorage.getItem('ledger_users');
        if (usersData) {
            const users: User[] = JSON.parse(usersData);
            const batch = writeBatch(db);
            users.forEach(user => {
                batch.set(doc(db, 'users', user.id), user);
            });
            await batch.commit();
            console.log(`Migrated ${users.length} users`);
        }

        // Migrate chat sessions
        const sessionsData = localStorage.getItem('ledger_sessions');
        if (sessionsData) {
            const sessions: ChatSession[] = JSON.parse(sessionsData);
            const batch = writeBatch(db);
            sessions.forEach(session => {
                batch.set(doc(db, 'chatSessions', session.id), session);
            });
            await batch.commit();
            console.log(`Migrated ${sessions.length} chat sessions`);
        }

        // Migrate config
        const configData = localStorage.getItem('ledger_config');
        if (configData) {
            const config: GlobalConfig = JSON.parse(configData);
            await setDoc(doc(db, 'config', 'global'), config);
            console.log('Migrated global config');
        }

        // Migrate promo codes
        const promosData = localStorage.getItem('ledger_promos');
        if (promosData) {
            const promos: PromoCode[] = JSON.parse(promosData);
            await setDoc(doc(db, 'config', 'promoCodes'), { codes: promos });
            console.log(`Migrated ${promos.length} promo codes`);
        }

        // Migrate ads
        const adsData = localStorage.getItem('ledger_ads');
        if (adsData) {
            const ads: AdBanner[] = JSON.parse(adsData);
            await setDoc(doc(db, 'config', 'ads'), { banners: ads });
            console.log(`Migrated ${ads.length} ads`);
        }

        console.log('Migration completed successfully!');
    } catch (error) {
        console.error('Error during migration:', error);
        throw error;
    }
};

// ==================== TRANSACTIONS ====================

/**
 * Create a new transaction record
 */
export const createTransaction = async (transaction: any): Promise<void> => {
    try {
        await setDoc(doc(db, 'transactions', transaction.id), transaction);
        console.log('Transaction created:', transaction.id);
    } catch (error) {
        console.error('Error creating transaction:', error);
        throw error;
    }
};

/**
 * Get all transactions (for admin)
 */
export const getAllTransactions = async (): Promise<any[]> => {
    try {
        const q = query(collection(db, 'transactions'), orderBy('createdAt', 'desc'));
        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => doc.data());
    } catch (error) {
        console.error('Error getting transactions:', error);
        throw error;
    }
};

/**
 * Approve transaction (Manual Admin Action)
 * Updates transaction status AND extends user subscription
 */
export const approveTransaction = async (transactionId: string, userId: string, months: number): Promise<void> => {
    try {
        // 1. Update Transaction Status
        await updateDoc(doc(db, 'transactions', transactionId), {
            status: 'approved_by_admin'
        });

        // 2. Extend User Subscription
        const userDoc = await getDoc(doc(db, 'users', userId));
        if (userDoc.exists()) {
            const userData = userDoc.data() as User;
            const now = new Date();
            let currentExpiry = userData.premiumExpiryDate ? new Date(userData.premiumExpiryDate) : now;

            // If expired, start from now
            if (currentExpiry < now) currentExpiry = now;

            // Add months
            currentExpiry.setMonth(currentExpiry.getMonth() + months);

            await updateDoc(doc(db, 'users', userId), {
                isPremium: true,
                premiumExpiryDate: currentExpiry.toISOString(),
                premiumStartDate: userData.premiumStartDate || now.toISOString()
            });

            console.log(`Transaction ${transactionId} approved. User ${userId} extended by ${months} months.`);
        }
    } catch (error) {
        console.error('Error approving transaction:', error);
        throw error;
    }
};

// ==================== SHOP CONFIG ====================

/**
 * Recursively remove undefined values from an object
 * Firebase doesn't accept undefined values
 */
const sanitizeForFirebase = (obj: any): any => {
    if (obj === null || obj === undefined) {
        return null;
    }
    if (Array.isArray(obj)) {
        return obj.map(sanitizeForFirebase);
    }
    if (typeof obj === 'object') {
        const sanitized: any = {};
        for (const [key, value] of Object.entries(obj)) {
            if (value !== undefined) {
                sanitized[key] = sanitizeForFirebase(value);
            }
        }
        return sanitized;
    }
    return obj;
};

/**
 * Save shop configuration to Firebase
 */
export const saveShopConfig = async (shopConfig: any): Promise<void> => {
    try {
        // Sanitize config to remove undefined values (Firebase doesn't accept them)
        const sanitizedConfig = sanitizeForFirebase(shopConfig);
        await setDoc(doc(db, 'config', 'shop'), sanitizedConfig);
        console.log('Shop config saved to Firebase successfully');
    } catch (error) {
        console.error('Error saving shop config to Firebase:', error);
        throw error;
    }
};

/**
 * Get shop configuration from Firebase
 */
export const getShopConfig = async (): Promise<any | null> => {
    try {
        const shopDoc = await getDoc(doc(db, 'config', 'shop'));
        if (shopDoc.exists()) {
            return shopDoc.data();
        }
        return null;
    } catch (error) {
        console.error('Error getting shop config from Firebase:', error);
        throw error;
    }
};

/**
 * Listen to shop config changes in real-time
 */
export const listenToShopConfig = (callback: (config: any | null) => void) => {
    return onSnapshot(doc(db, 'config', 'shop'), (doc) => {
        if (doc.exists()) {
            callback(doc.data());
        } else {
            callback(null);
        }
    });
};

// ==================== MASTERY STORE CONFIG ====================

/**
 * Save mastery store configuration to Firebase
 */
export const saveMasteryStoreConfig = async (config: any): Promise<void> => {
    try {
        const sanitizedConfig = sanitizeForFirebase(config);
        await setDoc(doc(db, 'config', 'masteryStore'), sanitizedConfig);
        console.log('Mastery store config saved to Firebase');
    } catch (error) {
        console.error('Error saving mastery store config:', error);
        throw error;
    }
};

/**
 * Get mastery store configuration from Firebase
 */
export const getMasteryStoreConfig = async (): Promise<any | null> => {
    try {
        const configDoc = await getDoc(doc(db, 'config', 'masteryStore'));
        if (configDoc.exists()) {
            return configDoc.data();
        }
        return null;
    } catch (error) {
        console.error('Error getting mastery store config:', error);
        return null;
    }
};

// ==================== LANDING PAGE CONFIG ====================

/**
 * Save landing page configuration to Firebase
 */
export const saveLandingConfig = async (landingConfig: any): Promise<void> => {
    try {
        await setDoc(doc(db, 'config', 'landing'), landingConfig);
        console.log('Landing config saved to Firebase successfully');
        // Also save to localStorage as cache
        localStorage.setItem('luca_landing_content_config', JSON.stringify(landingConfig));
        // Dispatch event for real-time updates
        window.dispatchEvent(new CustomEvent('landingConfigUpdated', { detail: landingConfig }));
    } catch (error) {
        console.error('Error saving landing config to Firebase:', error);
        throw error;
    }
};

/**
 * Get landing page configuration from Firebase
 */
export const getLandingConfig = async (): Promise<any | null> => {
    try {
        const landingDoc = await getDoc(doc(db, 'config', 'landing'));
        if (landingDoc.exists()) {
            const config = landingDoc.data();
            // Update localStorage cache
            localStorage.setItem('luca_landing_content_config', JSON.stringify(config));
            return config;
        }
        return null;
    } catch (error) {
        console.error('Error getting landing config from Firebase:', error);
        throw error;
    }
};

/**
 * Listen to landing config changes in real-time
 */
export const listenToLandingConfig = (callback: (config: any | null) => void) => {
    return onSnapshot(doc(db, 'config', 'landing'), (doc) => {
        if (doc.exists()) {
            const config = doc.data();
            // Update localStorage cache
            localStorage.setItem('luca_landing_content_config', JSON.stringify(config));
            callback(config);
        } else {
            callback(null);
        }
    });
};

// ==================== RANK TIERS CONFIG ====================

/**
 * Save rank tiers configuration to Firebase
 */
export const saveRankTiersConfig = async (rankTiers: any[]): Promise<void> => {
    try {
        await setDoc(doc(db, 'config', 'rankTiers'), { tiers: rankTiers, updatedAt: Date.now() });
        console.log('Rank tiers config saved to Firebase successfully');
        // Dispatch event for real-time updates
        window.dispatchEvent(new CustomEvent('rankTiersConfigUpdated', { detail: rankTiers }));
    } catch (error) {
        console.error('Error saving rank tiers config to Firebase:', error);
        throw error;
    }
};

/**
 * Get rank tiers configuration from Firebase
 */
export const getRankTiersConfig = async (): Promise<any[] | null> => {
    try {
        const rankDoc = await getDoc(doc(db, 'config', 'rankTiers'));
        if (rankDoc.exists()) {
            return rankDoc.data().tiers || null;
        }
        return null;
    } catch (error) {
        console.error('Error getting rank tiers config from Firebase:', error);
        throw error;
    }
};

/**
 * Listen to rank tiers config changes in real-time
 */
export const listenToRankTiersConfig = (callback: (tiers: any[] | null) => void) => {
    return onSnapshot(doc(db, 'config', 'rankTiers'), (doc) => {
        if (doc.exists()) {
            callback(doc.data().tiers || null);
        } else {
            callback(null);
        }
    });
};

// ==================== CENTRAL CONFIG (All-in-One) ====================

/**
 * Save entire central config to Firebase
 */
export const saveCentralConfigToFirebase = async (centralConfig: any): Promise<void> => {
    try {
        await setDoc(doc(db, 'config', 'central'), { ...centralConfig, updatedAt: Date.now() });
        console.log('Central config saved to Firebase successfully');
        // Also update localStorage
        localStorage.setItem('luca_central_config', JSON.stringify(centralConfig));
        // Dispatch event for real-time updates
        window.dispatchEvent(new CustomEvent('centralConfigUpdated', { detail: centralConfig }));
    } catch (error) {
        console.error('Error saving central config to Firebase:', error);
        throw error;
    }
};

/**
 * Get entire central config from Firebase
 */
export const getCentralConfigFromFirebase = async (): Promise<any | null> => {
    try {
        const centralDoc = await getDoc(doc(db, 'config', 'central'));
        if (centralDoc.exists()) {
            const config = centralDoc.data();
            // Update localStorage cache
            localStorage.setItem('luca_central_config', JSON.stringify(config));
            return config;
        }
        return null;
    } catch (error) {
        console.error('Error getting central config from Firebase:', error);
        throw error;
    }
};

/**
 * Listen to central config changes in real-time
 */
export const listenToCentralConfig = (callback: (config: any | null) => void) => {
    return onSnapshot(doc(db, 'config', 'central'), (doc) => {
        if (doc.exists()) {
            const config = doc.data();
            // Update localStorage cache
            localStorage.setItem('luca_central_config', JSON.stringify(config));
            callback(config);
        } else {
            callback(null);
        }
    });
};

// ==================== CONVENIENCE ALIASES ====================

/**
 * Alias for getGlobalConfig (for compatibility)
 */
export const getConfig = getGlobalConfig;

/**
 * Alias for updateDoc on config (for compatibility)
 */
export const updateConfig = async (updates: Partial<GlobalConfig>): Promise<void> => {
    try {
        const currentConfig = await getGlobalConfig();
        // Ensure we have a valid base config even if currentConfig is null
        const baseConfig: GlobalConfig = currentConfig || {
            // Token Limits
            freeDailyLimit: 10,
            freeTokenLimit: 50000,
            premiumTokenLimit: 500000,
            maxTokenLimit: 2000000,

            // Attachment Limits
            freeAttachmentLimit: 5,
            premiumFileLimit: 20,

            // AI Models
            freeModel: 'gemini-2.5-flash-lite',
            premiumModel: 'gemini-2.5-flash',
            maxModel: 'gemini-2.5-pro',

            // Pricing
            proPrice: 9.99,
            maxPrice: 29.99,

            // Sales
            isSaleActive: false,
            saleDiscountPercent: 0,

            // Token Refresh
            limitResetIntervalHours: 24,
            standardRefreshRate: 1000,
            premiumRefreshRate: 5000,
            maxRefreshRate: 10000,
            refreshIntervalMinutes: 60,

            // API
            apiKey: '',

            // Welcome
            welcomeTitle: 'Welcome to Luca AI',
            welcomeMessage: 'Your intelligent study companion',
            welcomeFeatures: ['Smart tutoring', 'Quiz generation', 'Study materials'],

            // Extension Discounts
            extensionDiscounts: {
                months3: 10,
                months6: 20,
                months12: 30
            },

            // Token Warning
            tokenWarningThreshold: 1000,

            // Auto Budget
            minBudgetPerRequest: 100,
            maxBudgetPerRequest: 10000,

            // PayPal
            paypal: {
                clientId: '',
                environment: 'sandbox'
            }
        };

        const updatedConfig: GlobalConfig = {
            ...baseConfig,
            ...updates,
            // Re-assert required fields in case they were undefined in baseConfig
            freeDailyLimit: updates.freeDailyLimit ?? baseConfig.freeDailyLimit ?? 10,
            freeTokenLimit: updates.freeTokenLimit ?? baseConfig.freeTokenLimit ?? 50000,
            freeAttachmentLimit: updates.freeAttachmentLimit ?? baseConfig.freeAttachmentLimit ?? 5,
            freeModel: updates.freeModel ?? baseConfig.freeModel ?? 'gemini-pro',
            premiumModel: updates.premiumModel ?? baseConfig.premiumModel ?? 'gemini-pro',
            proPrice: updates.proPrice ?? baseConfig.proPrice ?? 9.99,
            isSaleActive: updates.isSaleActive ?? baseConfig.isSaleActive ?? false,
            saleDiscountPercent: updates.saleDiscountPercent ?? baseConfig.saleDiscountPercent ?? 0
        };

        await saveGlobalConfig(updatedConfig);
    } catch (error) {
        console.error('Error updating config:', error);
        throw error;
    }
};

/**
 * Test Firebase connection - useful for debugging APK issues
 */
export const testFirebaseConnection = async (): Promise<{
    success: boolean;
    platform: string;
    firestoreConnected: boolean;
    canReadUsers: boolean;
    error?: string;
}> => {
    const isCapacitor = typeof (window as any).Capacitor !== 'undefined';
    const result = {
        success: false,
        platform: isCapacitor ? 'APK/Capacitor' : 'Web',
        firestoreConnected: false,
        canReadUsers: false,
        error: undefined as string | undefined
    };

    try {
        console.log('[Firebase Test] Starting connection test...');
        console.log('[Firebase Test] Platform:', result.platform);

        // Check if Firestore is initialized
        if (!db) {
            result.error = 'Firestore not initialized';
            return result;
        }

        result.firestoreConnected = true;
        console.log('[Firebase Test] Firestore instance exists');

        // Try to read from users collection
        const usersRef = collection(db, 'users');
        const testQuery = query(usersRef, limit(1));
        const snapshot = await getDocs(testQuery);

        result.canReadUsers = true;
        console.log('[Firebase Test] Can read users collection. Docs found:', snapshot.size);

        result.success = true;
        return result;
    } catch (error: any) {
        console.error('[Firebase Test] Error:', error);
        result.error = error.message || 'Unknown error';
        return result;
    }
};

/**
 * Push an announcement notification to all users
 * This is called when an admin creates/activates a new announcement
 */
export const pushAnnouncementToAllUsers = async (announcement: {
    id: string;
    title: string;
    message: string;
    type: 'info' | 'warning' | 'success' | 'promo';
    audience: 'all' | 'free' | 'premium';
}): Promise<{ success: boolean; usersNotified: number }> => {
    try {
        console.log('[Firebase] Pushing announcement to users:', announcement.title);

        // Get all users
        const usersSnapshot = await getDocs(collection(db, 'users'));
        const users = usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as User));

        // Filter users based on audience
        const targetUsers = users.filter(user => {
            if (announcement.audience === 'all') return true;
            if (announcement.audience === 'free') return !user.isPremium && !user.isMax;
            if (announcement.audience === 'premium') return user.isPremium || user.isMax;
            return true;
        });

        // Create notification for each user
        const notification: UserNotification = {
            id: `ann-notif-${announcement.id}-${Date.now()}`,
            type: 'announcement',
            title: announcement.title,
            message: announcement.message.length > 100
                ? announcement.message.substring(0, 100) + '...'
                : announcement.message,
            timestamp: Date.now(),
            read: false,
            announcementId: announcement.id,
            fullContent: announcement.message
        };

        // Use batch write for efficiency (max 500 per batch)
        const batchSize = 500;
        let usersNotified = 0;

        for (let i = 0; i < targetUsers.length; i += batchSize) {
            const batch = writeBatch(db);
            const batchUsers = targetUsers.slice(i, i + batchSize);

            for (const user of batchUsers) {
                const userRef = doc(db, 'users', user.id);
                const existingNotifications = user.notifications || [];

                // Limit notifications to last 50 to prevent unbounded growth
                const updatedNotifications = [notification, ...existingNotifications].slice(0, 50);

                batch.update(userRef, { notifications: updatedNotifications });
                usersNotified++;
            }

            await batch.commit();
            console.log(`[Firebase] Batch ${Math.floor(i / batchSize) + 1} committed: ${batchUsers.length} users`);
        }

        console.log(`[Firebase] Announcement pushed to ${usersNotified} users`);
        return { success: true, usersNotified };
    } catch (error) {
        console.error('[Firebase] Error pushing announcement to users:', error);
        return { success: false, usersNotified: 0 };
    }
};

// ==================== NOTIFICATION CONFIGS ====================

/**
 * Save notification configurations
 */
export const saveNotificationConfigs = async (configs: NotificationConfig[]): Promise<void> => {
    try {
        await setDoc(doc(db, 'config', 'notifications'), { configs });
        console.log('Notification configs saved successfully');
    } catch (error) {
        console.error('Error saving notification configs:', error);
        throw error;
    }
};

/**
 * Get notification configurations
 */
export const getNotificationConfigs = async (): Promise<NotificationConfig[]> => {
    try {
        const docSnap = await getDoc(doc(db, 'config', 'notifications'));
        if (docSnap.exists()) {
            return (docSnap.data() as any).configs || [];
        }
        return [];
    } catch (error) {
        console.error('Error getting notification configs:', error);
        return [];
    }
};

/**
 * Listen to notification configurations
 */
export const listenToNotificationConfigs = (callback: (configs: NotificationConfig[]) => void) => {
    return onSnapshot(doc(db, 'config', 'notifications'), (doc) => {
        if (doc.exists()) {
            callback((doc.data() as any).configs || []);
        } else {
            callback([]);
        }
    });
};
// ==================== CHAT BACKGROUNDS ====================

/**
 * Save chat backgrounds configuration
 */
export const saveChatBackgrounds = async (backgrounds: any[]): Promise<void> => {
    try {
        await setDoc(doc(db, 'config', 'chatBackgrounds'), { backgrounds });
        console.log('Chat backgrounds saved successfully');
    } catch (error) {
        console.error('Error saving chat backgrounds:', error);
        throw error;
    }
};

/**
 * Get chat backgrounds configuration
 */
export const getChatBackgrounds = async (): Promise<any[]> => {
    try {
        const docSnap = await getDoc(doc(db, 'config', 'chatBackgrounds'));
        if (docSnap.exists()) {
            return (docSnap.data() as any).backgrounds || [];
        }
        return [];
    } catch (error) {
        console.error('Error getting chat backgrounds:', error);
        return [];
    }
};

/**
 * Listen to chat backgrounds configuration
 */
export const listenToChatBackgrounds = (callback: (backgrounds: any[]) => void) => {
    return onSnapshot(doc(db, 'config', 'chatBackgrounds'), (doc) => {
        if (doc.exists()) {
            callback((doc.data() as any).backgrounds || []);
        } else {
            callback([]);
        }
    });
};
